import type { TrendDiscoveryJobSummary } from './brandIntel';
import type { StorylineResponse, TrendingTopicsResponse } from './metrics';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { toast } from '@/lib/toast';
import i18n from '@/i18n';
import {
  Brand,
  CreateBrandRequest,
  UpdateBrandRequest,
  Post,
  CreatePostRequest,
  UpdatePostRequest,
  GeneratePostsRequest,
  GeneratePostsResponse,
  Workspace,
  CreateWorkspaceRequest,
  DashboardStats,
  User,
  PlatformConnection,
  OnboardingProfile,
  ApiKey,
  DataEventItem,
  ContentCadence,
  UpsertCadenceRequest,
  ReviewPost,
  AnalyticsOverview,
  PostAnalyticsItem,
  PostTimeseriesPoint,
  PlatformTimeseriesPoint,
  LinkedInOrgSummary,
  LinkedInOrgHistory,
  SocialPlatform,
  AdCampaign,
  AdSet,
  AdCreative,
  CampaignInsightsResponse,
  AdminUser,
  AdminUsageOverview,
  ManualPostRequest,
  WorkspaceAiUsage,
  AdminAiUsageSummary,
  AdminAiSettingsResponse,
  UpdateAdminAiSettingsRequest,
  AdminWorkspaceAiUsage,
  SchedulerConfig,
  ApiCallLogRow,
  ApiCallLogStats,
  ImportJob,
  BlogAnalyticsSummary,
} from '@/types';

import { api } from './client';

export const storylineApi = {
  create: (
    workspaceId: string,
    data: { brandId: string; name: string; goal?: string; targetDate: string; platforms: string[] },
  ) =>
    api
      .post<StorylineResponse>(`/api/workspaces/${workspaceId}/storylines`, data)
      .then((r) => r.data),
  list: (workspaceId: string, params?: { brandId?: string; status?: string }) =>
    api
      .get<StorylineResponse[]>(`/api/workspaces/${workspaceId}/storylines`, { params })
      .then((r) => r.data),
  get: (workspaceId: string, storylineId: string) =>
    api
      .get<StorylineResponse>(`/api/workspaces/${workspaceId}/storylines/${storylineId}`)
      .then((r) => r.data),
  update: (
    workspaceId: string,
    storylineId: string,
    data: { name?: string; goal?: string; targetDate?: string; status?: string },
  ) =>
    api
      .patch<StorylineResponse>(`/api/workspaces/${workspaceId}/storylines/${storylineId}`, data)
      .then((r) => r.data),
  delete: (workspaceId: string, storylineId: string) =>
    api
      .delete<StorylineResponse>(`/api/workspaces/${workspaceId}/storylines/${storylineId}`)
      .then((r) => r.data),
  /** Polish brief Step 5.1 — hard delete an already-archived storyline.
   *  Backend returns 409 for non-archived storylines. */
  deletePermanently: (workspaceId: string, storylineId: string) =>
    api
      .delete<void>(`/api/workspaces/${workspaceId}/storylines/${storylineId}/permanent`)
      .then((r) => r.data),
  confirmArc: (
    workspaceId: string,
    storylineId: string,
    posts: {
      position: number;
      suggestedDate: string;
      arcBeat: string;
      contentType: string;
      copyStub?: string;
      copyStubNl?: string;
      copyStubEn?: string;
      platform?: string;
    }[],
  ) =>
    api
      .post<StorylineResponse>(
        `/api/workspaces/${workspaceId}/storylines/${storylineId}/confirm-arc`,
        { posts },
      )
      .then((r) => r.data),
  regenerateArc: (workspaceId: string, storylineId: string) =>
    api
      .post<StorylineResponse>(
        `/api/workspaces/${workspaceId}/storylines/${storylineId}/regenerate-arc`,
      )
      .then((r) => r.data),
  rebuildPosts: (workspaceId: string, storylineId: string) =>
    api
      .post<StorylineResponse>(
        `/api/workspaces/${workspaceId}/storylines/${storylineId}/rebuild-posts`,
      )
      .then((r) => r.data),
};

// ── Post media upload ───────────────────────────────────────────────────

export const postMediaApi = {
  upload: (workspaceId: string, postId: string, file: File, mediaType: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mediaType', mediaType);
    return api
      .post<Post>(`/api/workspaces/${workspaceId}/posts/${postId}/upload-media`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },
  remove: (workspaceId: string, postId: string) =>
    api
      .delete<Post>(`/api/workspaces/${workspaceId}/posts/${postId}/upload-media`)
      .then((r) => r.data),
};

export const discoverApi = {
  getTrending: (workspaceId: string, brandId?: string) =>
    api
      .get<TrendingTopicsResponse>(`/api/workspaces/${workspaceId}/discover/trending`, {
        params: brandId ? { brandId } : {},
      })
      .then((r) => r.data),
  refresh: (workspaceId: string, brandId?: string) =>
    api
      .post<{
        status: string;
        jobId?: string;
      }>(`/api/workspaces/${workspaceId}/discover/refresh`, brandId ? { brandId } : {})
      .then((r) => r.data),
  /** Latest trend-discovery job for the workspace (or filtered by
   *  brandId). Drives the "Vernieuwen…" banner across page navigation. */
  refreshStatus: (workspaceId: string, brandId?: string) =>
    api
      .get<TrendDiscoveryJobSummary>(`/api/workspaces/${workspaceId}/discover/refresh-status`, {
        params: brandId ? { brandId } : {},
      })
      .then((r) => r.data),
};

// ── Admin RAG dashboard (BRIEF-admin-rag-dashboard-v2) ─────────────────────

export interface AdminRagHealth {
  embeddingCoverage: {
    totalPublished: number;
    withEmbedding: number;
    withoutEmbedding: number;
    coveragePct: number;
  };
  brandMemoryFreshness: {
    totalPairs: number;
    stalePairs: number;
    stalestBrand: string | null;
    stalestDays: number | null;
    staleThresholdDays: number;
  };
  traceStats: {
    total: number;
    last7Days: number;
    avgRetrievalDurationMs: number;
    avgSimilarPostsReturned: number;
    pctWithTopPerformers: number;
    pctWithRecurringThemes: number;
    pctWithStylePatterns: number;
    pctWithSimilarPosts: number;
  };
  embeddings30d: { embeddingsLast30Days: number; model: string };
}

export interface AdminRagTraceRow {
  id: string;
  postId: string | null;
  brandName: string;
  platform: string;
  contentType: string | null;
  queryText: string | null;
  embeddingModel: string | null;
  cutoffUsed: number | null;
  similarPostsReturned: number;
  avgDistance: number | null;
  usedRecurringThemes: boolean;
  usedStylePatterns: boolean;
  recentTopicsCount: number;
  topicGapsCount: number;
  retrievalDurationMs: number | null;
  promptTokenCount: number | null;
  ragTokenCount: number | null;
  createdAt: string;
}

export interface AdminRagTraceDetail {
  trace: AdminRagTraceRow;
  similarPosts: Array<{
    id: string;
    copy: string;
    platform: string;
    distance: number;
    publishedAt: string | null;
    engagementScore: number | null;
  }>;
  topPerformers: Array<{
    id: string;
    copy: string;
    platform: string;
    likes: number | null;
    comments: number | null;
    shares: number | null;
  }>;
  generatedPost: {
    id: string;
    copy: string;
    status: string | null;
    geoCitabilityScore: number | null;
  } | null;
}

export interface AdminRagBrandMemoryEntry {
  brandId: string;
  brandName: string;
  platform: string;
  totalPosts: number;
  topTopics: string[];
  recurringThemes: string | null;
  stylePatterns: string | null;
  lastUpdated: string | null;
  daysSinceUpdate: number | null;
  isStale: boolean;
  postsWithEmbedding: number;
  postsWithoutEmbedding: number;
}

export interface AdminRagEmbeddingCoverageBrand {
  brandId: string;
  brandName: string;
  /** V121 follow-up — admin RAG page is platform-wide (every workspace),
   *  so the brand picker prefixes the workspace name for context. */
  workspaceId: string;
  workspaceName: string;
  totalPublished: number;
  withEmbedding: number;
  withoutEmbedding: number;
  coveragePct: number;
  oldestUnembedded: string | null;
}

export interface AdminRagProjectionPoint {
  postId: string;
  x: number;
  y: number;
  platform: string;
  contentType: string | null;
  copyPreview: string;
  publishedAt: string | null;
  geoCitabilityScore: number | null;
}

export interface AdminRagProjectionResult {
  points: AdminRagProjectionPoint[];
  totalPosts: number;
  embeddedPosts: number;
  computeTimeMs: number;
  algorithm: string;
  parameters: Record<string, unknown>;
}

export const adminRagApi = {
  getHealth: () => api.get<AdminRagHealth>('/api/admin/rag/health').then((r) => r.data),
  listTraces: (params?: {
    brandId?: string;
    platform?: string;
    page?: number;
    size?: number;
    from?: string;
    to?: string;
  }) =>
    api.get<AdminRagTraceRow[]>('/api/admin/rag/traces', { params }).then((r) => ({
      traces: r.data,
      total: parseInt(r.headers['x-total-count'] ?? '0', 10),
    })),
  getTrace: (traceId: string) =>
    api.get<AdminRagTraceDetail>(`/api/admin/rag/traces/${traceId}`).then((r) => r.data),
  getBrandMemory: () =>
    api
      .get<{ entries: AdminRagBrandMemoryEntry[] }>('/api/admin/rag/brand-memory')
      .then((r) => r.data),
  refreshBrandMemory: (brandId: string, platform: string) =>
    api
      .post<{
        status: string;
      }>(`/api/admin/rag/brand-memory/${brandId}/refresh`, null, { params: { platform } })
      .then((r) => r.data),
  getEmbeddingCoverage: () =>
    api
      .get<{
        brands: AdminRagEmbeddingCoverageBrand[];
        global: AdminRagHealth['embeddingCoverage'];
      }>('/api/admin/rag/embedding-coverage')
      .then((r) => r.data),
  getVectorMap: (brandId: string) =>
    api
      .get<AdminRagProjectionResult>('/api/admin/rag/vector-map', { params: { brandId } })
      .then((r) => r.data),
  recomputeVectorMap: (brandId: string) =>
    api
      .post<AdminRagProjectionResult>('/api/admin/rag/vector-map/recompute', null, {
        params: { brandId },
      })
      .then((r) => r.data),
};

// ─── V152 — AI Chatbot (Merkflow Copilot) ─────────────────────────
//
// Two roots, matching the backend split:
//   • workspace-scoped POST + limits (cost + monthly cap belong to a
//     specific workspace's tier)
//   • user-scoped list / read / delete (chats are user data, follow
//     the user across workspaces)

export interface ChatConversationSummary {
  id: string;
  title: string | null;
  updatedAt: string | null;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  createdAt: string | null;
}

export interface ChatConversationDetail {
  id: string;
  title: string | null;
  messages: ChatMessage[];
  createdAt: string | null;
  updatedAt: string | null;
}

export interface ChatActionResponse {
  type: string;
  summary: string;
  postIds: string[];
}

export interface ChatMessageResponse {
  conversationId: string;
  message: string;
  model: string;
  /** V153 — side effects the assistant performed (e.g. drafts created in
   *  the Review inbox). Empty for plain text replies. */
  actions: ChatActionResponse[];
}

export interface ChatbotLimitsResponse {
  messagesUsedThisMonth: number;
  /** -1 = unlimited. */
  messagesLimitThisMonth: number;
  conversationCount: number;
  conversationLimit: number;
}

export const chatbotApi = {
  sendMessage: (workspaceId: string, body: { conversationId?: string; message: string }) =>
    api
      .post<ChatMessageResponse>(`/api/workspaces/${workspaceId}/chat/messages`, body)
      .then((r) => r.data),
  getLimits: (workspaceId: string) =>
    api
      .get<ChatbotLimitsResponse>(`/api/workspaces/${workspaceId}/chat/limits`)
      .then((r) => r.data),
  listConversations: () =>
    api.get<ChatConversationSummary[]>(`/api/chat/conversations`).then((r) => r.data),
  getConversation: (conversationId: string) =>
    api
      .get<ChatConversationDetail>(`/api/chat/conversations/${conversationId}`)
      .then((r) => r.data),
  deleteConversation: (conversationId: string) =>
    api.delete(`/api/chat/conversations/${conversationId}`).then((r) => r.data),
};

export default api;
